import { useAuthController } from '@/features/auth/controller/auth.controller';
import { API_BASE_URL } from '@/app/constants/apiBaseUrl';
import { Config } from '@/app/constants/config';
import { i18n } from '@/i18n';
import { SUPPORTED_LANGUAGES } from '@/languages';
import {
    clearNativeTokens,
    getNativeAccessToken,
    getNativeRefreshToken,
    isNativeTokenResponse,
    saveNativeTokens,
    usesBearerAuth,
} from '@/services/nativeSession';
import { idempotencyKeyFor, releaseIdempotencyKey } from '@/services/idempotencyKeys';
import { appVersionHeaders } from '@/services/appVersion';
import { APP_UPDATE_REQUIRED, useAppUpdateRequired } from '@/features/native-app/model/appUpdateRequired';

export { API_BASE_URL };

export const DEFAULT_REQUEST_TIMEOUT_MS = Config.api.timeout;

/** Bad gateway / gateway timeout: the proxy answered, the API may still have applied the write. */
const PROXY_CUT_STATUSES = new Set([502, 504]);

/**
 * Erreur HTTP typée propagée par `request<T>()`.
 * Contient le status, le body parsé (inconnu) et un message fallback.
 * Les composants UI peuvent lire `.status` pour adapter l'affichage,
 * et afficher un `t()` en fonction du code.
 */
export class HttpError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(status: number, message: string, body?: unknown) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
        this.body = body;
    }
}

/** Business error code (`code` field) of a backend error body, if any. */
export function httpErrorCode(error: unknown): string | undefined {
    if (!(error instanceof HttpError)) return undefined;
    const { body } = error;
    return typeof body === 'object' && body !== null && 'code' in body && typeof body.code === 'string'
        ? body.code
        : undefined;
}

/** Values sent with a backend error code (`params` field), for its translated message. */
export function httpErrorParams(error: unknown): Record<string, string | number> {
    if (!(error instanceof HttpError)) return {};
    const { body } = error;
    if (typeof body !== 'object' || body === null || !('params' in body)) return {};
    const { params } = body;
    if (typeof params !== 'object' || params === null) return {};
    return Object.fromEntries(
        Object.entries(params).filter(
            (entry): entry is [string, string | number] =>
                typeof entry[1] === 'string' || typeof entry[1] === 'number',
        ),
    );
}

/** i18n key for a backend error code listed in `keys`, if any. */
export function errorKeyFor<K extends string>(error: unknown, keys: Readonly<Record<string, K>>): K | undefined {
    const code = httpErrorCode(error);
    return code !== undefined && Object.hasOwn(keys, code) ? keys[code] : undefined;
}

/**
 * Erreur dédiée à la perte de session (401 non récupérable).
 * Le handler global se charge de logout + redirect.
 */
export class SessionExpiredError extends HttpError {
    constructor(message = 'Session expirée') {
        super(401, message);
        this.name = 'SessionExpiredError';
    }
}

/**
 * The server did not answer within the allotted time. Distinct from a network
 * failure: the request may have been processed, so only idempotent calls should
 * be retried blindly. Its message is translated: several screens show
 * `error.message` to the coach as is. A read has nothing to check, only a write
 * may have gone through.
 */
export class RequestTimeoutError extends Error {
    readonly timeoutMs: number;

    constructor(timeoutMs: number, method = 'GET', replayable = false) {
        super(i18n.t(replayable || method.toUpperCase() === 'GET' ? 'common:errors.http.timeout_read' : 'common:errors.http.timeout'));
        this.name = 'RequestTimeoutError';
        this.timeoutMs = timeoutMs;
    }
}

export interface HttpRequestOptions extends Omit<RequestInit, 'body'> {
    /**
     * Max wait for the whole request (session refresh, retry and body included), in ms.
     * Defaults to `DEFAULT_REQUEST_TIMEOUT_MS`; `null` disables it for calls the
     * backend may legitimately keep longer (DataVolley import).
     */
    timeoutMs?: number | null;
    /**
     * Sending the same write again does no harm (a PUT of the whole team): on a
     * timeout the coach is told to retry, not to check first (VOL-324).
     */
    replayable?: boolean;
    /**
     * A create the server deduplicates (VOL-324): the same body sent again before
     * any answer (a retry after a timeout) carries the same `Idempotency-Key`, and
     * gets the first reply instead of a duplicate. Implies `replayable`.
     */
    idempotent?: boolean;
    /** Body JSON sérialisé automatiquement. Pour un body brut, passer via `rawBody`. */
    json?: unknown;
    /** Body brut (FormData, string, etc.) — non sérialisé. */
    rawBody?: BodyInit;
    /** Désactive l'injection `credentials: 'include'` (rarement utile). */
    skipCredentials?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function extractErrorMessage(body: unknown, fallback: string): string {
    if (!isRecord(body)) return fallback;
    if (typeof body.message === 'string' && body.message.length > 0) {
        return body.message;
    }
    // NestJS's ValidationPipe sends one message per failed constraint, as an array.
    if (Array.isArray(body.message)) {
        const first: unknown = body.message[0];
        if (typeof first === 'string' && first.length > 0) return first;
    }
    return fallback;
}

async function safeJson(response: Response): Promise<unknown> {
    try {
        const parsed: unknown = await response.json();
        return parsed;
    } catch {
        return undefined;
    }
}

interface Deadline {
    signal: AbortSignal | undefined;
    timedOut: () => boolean;
    clear: () => void;
}

/**
 * Abort signal combining the caller's own signal with a timeout. Built by hand
 * rather than with `AbortSignal.any` (Safari 17.4+), which older club tablets lack.
 */
function createDeadline(timeoutMs: number | null, callerSignal?: AbortSignal | null): Deadline {
    if (timeoutMs === null) {
        return { signal: callerSignal ?? undefined, timedOut: () => false, clear: () => {} };
    }

    const controller = new AbortController();
    let expired = false;
    const onCallerAbort = () => controller.abort(callerSignal?.reason);
    if (callerSignal?.aborted) {
        controller.abort(callerSignal.reason);
    } else {
        callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
    }
    const timer = setTimeout(() => {
        expired = true;
        controller.abort();
    }, timeoutMs);

    return {
        signal: controller.signal,
        timedOut: () => expired,
        clear: () => {
            clearTimeout(timer);
            callerSignal?.removeEventListener('abort', onCallerAbort);
        },
    };
}

/**
 * Stops waiting on a shared promise once `signal` aborts, without cancelling it
 * for the other callers waiting on it.
 */
function untilAborted<T>(promise: Promise<T>, signal: AbortSignal | null | undefined): Promise<T> {
    if (!signal) return promise;
    return new Promise<T>((resolve, reject) => {
        // Same rejection as an aborted `fetch`, so callers tell their own abort apart.
        const onAbort = () => reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
        if (signal.aborted) {
            // The shared refresh may still reject: nobody else may be listening.
            promise.catch(() => undefined);
            onAbort();
            return;
        }
        signal.addEventListener('abort', onAbort, { once: true });
        promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort));
    });
}

type RefreshOutcome = 'refreshed' | 'failed' | 'timeout';

/**
 * Only a refused refresh ends the session. A 5xx (API restarting during a deploy),
 * a 408 or a 429 says nothing about it.
 */
function isRefreshRefused(status: number): boolean {
    return status >= 400 && status < 500 && status !== 408 && status !== 429;
}

let refreshPromise: Promise<RefreshOutcome> | null = null;

function refreshCookieSession(signal: AbortSignal | undefined): Promise<RefreshOutcome> {
    return fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        signal,
    }).then((res): RefreshOutcome => {
        if (res.ok) return 'refreshed';
        if (isRefreshRefused(res.status)) return 'failed';
        throw new HttpError(res.status, `HTTP ${res.status}`);
    });
}

/**
 * Native app: the refresh token comes from the secure storage and is rotated by
 * every refresh. A refused one is dropped, since the server will never take it again.
 */
async function refreshNativeSession(signal: AbortSignal | undefined): Promise<RefreshOutcome> {
    const refreshToken = await getNativeRefreshToken();
    if (!refreshToken) return 'failed';

    const res = await fetch(`${API_BASE_URL}/auth/native/refresh`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
        signal,
    });
    if (res.ok) {
        const body = await safeJson(res);
        if (!isNativeTokenResponse(body)) throw new HttpError(res.status, 'Réponse non JSON', body);
        await saveNativeTokens(body);
        return 'refreshed';
    }
    if (isRefreshRefused(res.status)) {
        await clearNativeTokens();
        return 'failed';
    }
    throw new HttpError(res.status, `HTTP ${res.status}`);
}

/**
 * Shared session refresh. Rejects when the server could not be reached or failed
 * temporarily: the caller then fails like any network error, without logging the
 * coach out (a dropped connection mid-match must not end the session).
 */
async function tryRefresh(): Promise<RefreshOutcome> {
    if (refreshPromise) return refreshPromise;

    const deadline = createDeadline(DEFAULT_REQUEST_TIMEOUT_MS);
    // One refresh at a time also matters to the native token: of two concurrent
    // rotations of the same refresh token, the server refuses the second one.
    refreshPromise = (usesBearerAuth() ? refreshNativeSession(deadline.signal) : refreshCookieSession(deadline.signal))
        // A refresh the server never answered says nothing about the session either.
        .catch((error: unknown): RefreshOutcome => {
            if (deadline.timedOut()) return 'timeout';
            throw error;
        })
        .finally(() => {
            deadline.clear();
            refreshPromise = null;
        });

    return refreshPromise;
}

/**
 * Keeps the session alive ahead of expiry (`useSessionKeepAlive`). Shares the
 * refresh in flight, and never logs out: a refused refresh is left to the next 401.
 */
export async function refreshSession(): Promise<void> {
    await tryRefresh();
}

/**
 * Proves the session on a request: untouched on the web (httpOnly cookies), the
 * Bearer token and no cookie at all in the native app. Applied at send time, so a
 * request replayed after a refresh carries the new token.
 */
export function withSessionAuth(init: RequestInit): RequestInit {
    if (!usesBearerAuth()) return init;
    const headers = new Headers(init.headers);
    const token = getNativeAccessToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return { ...init, credentials: 'omit', headers };
}

/**
 * The app language, not the browser's list: the back writes the emails of a
 * sign-up or a forgotten password in it, before the account stores one (VOL-326).
 */
function languageHeader(): Record<string, string> {
    const current = i18n.resolvedLanguage ?? i18n.language;
    const language = SUPPORTED_LANGUAGES.find((l) => current?.startsWith(l));
    return language ? { 'Accept-Language': language } : {};
}

function isFormData(body: unknown): boolean {
    return typeof FormData !== 'undefined' && body instanceof FormData;
}

function buildInit(options: HttpRequestOptions | undefined, extraHeaders: Record<string, string> = {}): RequestInit {
    const {
        json,
        rawBody,
        skipCredentials,
        headers,
        timeoutMs: _timeoutMs,
        replayable: _replayable,
        idempotent: _idempotent,
        ...rest
    } = options ?? {};
    const formBody = rawBody !== undefined && isFormData(rawBody);
    const merged: RequestInit = {
        ...rest,
        credentials: skipCredentials ? undefined : 'include',
        headers: formBody
            ? { ...languageHeader(), ...(headers ?? {}), ...extraHeaders }
            : {
                  'Content-Type': 'application/json',
                  ...languageHeader(),
                  ...(headers ?? {}),
                  ...extraHeaders,
              },
    };
    if (rawBody !== undefined) {
        merged.body = rawBody;
    } else if (json !== undefined) {
        merged.body = JSON.stringify(json);
    }
    return merged;
}

/**
 * Wrapper HTTP typé central — utilisé par tous les services.
 *
 * Contrat :
 * - Succès 2xx avec body JSON → renvoie le JSON typé `T`.
 * - Succès 204 No Content → renvoie `undefined` typé `T` (l'appelant doit typer `T` en conséquence).
 * - Erreur HTTP (>=400, hors 401 géré) → throw `HttpError` avec status + body parsé.
 * - Erreur 401 non récupérable → logout + redirect `/login` + throw `SessionExpiredError`.
 * - Body non-JSON sur une réponse OK → throw `HttpError` (parsing impossible).
 * - No answer within `timeoutMs` → throw `RequestTimeoutError`.
 *
 * Règle §16 (TypeScript) : ce fichier contient le SEUL `as T` centralisé
 * et documenté du client HTTP. Tous les autres services doivent passer par
 * `request<T>()` et ne jamais utiliser `as` sur une réponse fetch.
 */
export async function request<T>(
    path: string,
    options?: HttpRequestOptions,
): Promise<T> {
    const timeoutMs = options?.timeoutMs === undefined ? DEFAULT_REQUEST_TIMEOUT_MS : options.timeoutMs;
    const deadline = createDeadline(timeoutMs, options?.signal);
    const fingerprint = options?.idempotent
        ? `${options.method ?? 'GET'} ${path} ${JSON.stringify(options.json ?? null)}`
        : undefined;
    const extraHeaders: Record<string, string> = fingerprint
        ? { 'Idempotency-Key': idempotencyKeyFor(fingerprint) }
        : {};
    try {
        const result = await send<T>(path, { ...buildInit(options, extraHeaders), signal: deadline.signal });
        if (fingerprint) releaseIdempotencyKey(fingerprint);
        return result;
    } catch (error) {
        // Kept only while the server may still have applied it: timeout, network, abort,
        // and a 502/504 from nginx, which can cut the API off after it has written.
        if (fingerprint && error instanceof HttpError && !PROXY_CUT_STATUSES.has(error.status)) {
            releaseIdempotencyKey(fingerprint);
        }
        // An error answer whose body read was cut by the deadline keeps its real status.
        const answeredWithError = error instanceof HttpError && error.status >= 400;
        if (timeoutMs !== null && deadline.timedOut() && !answeredWithError) {
            throw new RequestTimeoutError(timeoutMs, options?.method, options?.replayable || options?.idempotent);
        }
        // `fetch` rejects with the caller's own abort reason: a custom one would
        // not read as an abort, and the global toast would report it (VOL-324).
        const callerSignal = options?.signal;
        if (callerSignal?.aborted && error === callerSignal.reason && !(error instanceof DOMException)) {
            throw Object.assign(new DOMException('Aborted', 'AbortError'), { cause: error });
        }
        throw error;
    } finally {
        deadline.clear();
    }
}

async function withAppVersion(init: RequestInit): Promise<RequestInit> {
    const extra = Object.entries(await appVersionHeaders());
    if (extra.length === 0) return init;
    const headers = new Headers(init.headers);
    for (const [name, value] of extra) headers.set(name, value);
    return { ...init, headers };
}

/**
 * A file download (`.dvw`): the session, app version and refresh of `request`,
 * with the binary body left to the caller. Throws `HttpError` on an error answer.
 */
export async function requestFile(path: string): Promise<Response> {
    const response = await exchange(path, { method: 'GET', credentials: 'include', headers: languageHeader() });
    if (!response.ok) throw await errorFrom(response);
    return response;
}

async function send<T>(path: string, unversionedInit: RequestInit): Promise<T> {
    const response = await exchange(path, unversionedInit);

    if (!response.ok) throw await errorFrom(response);

    if (response.status === 204) {
        // 204 No Content — pas de body par spec HTTP.
        // L'appelant doit typer `T` comme `void`, `undefined`, ou un type acceptant l'absence de valeur.
        return undefined as T;
    }

    const data = await safeJson(response);
    if (data === undefined) {
        throw new HttpError(response.status, 'Réponse non JSON', undefined);
    }

    // CAST UNIQUE CENTRALISÉ — règle §16 TypeScript.
    // Le backend est source de vérité du schéma. Le typage `T` est porté par les
    // signatures des services (ex: `api.getMatches(): Promise<Match[]>`).
    // Pour valider le schéma à runtime, introduire zod (non présent à ce jour).
    return data as T;
}

async function errorFrom(response: Response): Promise<HttpError> {
    const body = await safeJson(response);
    const message = extractErrorMessage(body, `HTTP ${response.status}`);
    const error = new HttpError(response.status, message, body);
    if (response.status === 426 && httpErrorCode(error) === APP_UPDATE_REQUIRED) {
        useAppUpdateRequired.getState().markRequired();
    }
    return error;
}

/** Sends with the session proof, refreshing it once on a 401; an expired session logs out. */
async function exchange(path: string, unversionedInit: RequestInit): Promise<Response> {
    // Bearer is the native app's transport: the website never waits on this.
    const init = usesBearerAuth() ? await withAppVersion(unversionedInit) : unversionedInit;
    let response = await fetch(`${API_BASE_URL}${path}`, withSessionAuth(init));

    if (response.status === 401 && path !== '/auth/refresh') {
        // The refresh is shared and has its own 30 s limit: a caller with a
        // shorter one (live capture) must not be held past it.
        const refreshed = await untilAborted(tryRefresh(), init.signal);
        if (refreshed === 'timeout') throw new RequestTimeoutError(DEFAULT_REQUEST_TIMEOUT_MS, init.method);
        if (refreshed === 'refreshed') {
            response = await fetch(`${API_BASE_URL}${path}`, withSessionAuth(init));
        } else {
            useAuthController.getState().logout();
            if (typeof window !== 'undefined') {
                window.location.href = '/login';
            }
            throw new SessionExpiredError();
        }
    }

    if (response.status === 401) {
        useAuthController.getState().logout();
        if (typeof window !== 'undefined') {
            window.location.href = '/login';
        }
        throw new SessionExpiredError();
    }

    return response;
}

/**
 * A delete retried after a timeout may find the resource already deleted by the
 * first send: its 404 means the coach's request is done (VOL-324).
 */
export async function deleteIfPresent(path: string): Promise<void> {
    try {
        await request<void>(path, { method: 'DELETE', replayable: true });
    } catch (error) {
        if (error instanceof HttpError && error.status === 404) return;
        throw error;
    }
}

export const http = {
    get: <T>(path: string, options?: HttpRequestOptions): Promise<T> =>
        request<T>(path, { ...options, method: 'GET' }),
    post: <T>(path: string, json?: unknown, options?: HttpRequestOptions): Promise<T> =>
        request<T>(path, { ...options, method: 'POST', json }),
    put: <T>(path: string, json?: unknown, options?: HttpRequestOptions): Promise<T> =>
        request<T>(path, { ...options, method: 'PUT', json }),
    patch: <T>(path: string, json?: unknown, options?: HttpRequestOptions): Promise<T> =>
        request<T>(path, { ...options, method: 'PATCH', json }),
    delete: <T>(path: string, options?: HttpRequestOptions): Promise<T> =>
        request<T>(path, { ...options, method: 'DELETE' }),
};
